/**
 * TopicScroller Module
 * Handles smooth scrolling to topic anchors and track circle positioning
 */

export default class TopicScroller {
  constructor(listNavEl) {
    this.listNavEl = listNavEl
    this.init()
  }

  init() {
    // Disabled: Don't update active topic on scroll - only on manual click
    // this.setupScrollListener()
  }

  /**
   * Smooth scroll to an anchor on the page
   * @param {string} anchor - The anchor (e.g., '#laering')
   */
  scrollToAnchor(anchor) {
    // Find the target element
    const targetSelector = anchor.startsWith('#') ? anchor : `#${anchor}`
    const target = document.querySelector(targetSelector)

    if (!target) {
      // Try finding by data-topic attribute (fallback)
      const topicHandle = anchor.replace('#', '')
      const topicSection = document.querySelector(`[data-topic="${topicHandle}"]`)

      if (topicSection) {
        this.smoothScrollTo(topicSection)
        return
      }

      console.warn('[TopicScroller] Target not found:', anchor)
      return
    }

    this.smoothScrollTo(target)
  }

  /**
   * Smooth scroll to an element with offset for fixed header
   * @param {HTMLElement} element - The element to scroll to
   */
  smoothScrollTo(element) {
    const elementPosition = element.getBoundingClientRect().top

    // Get scroll-padding-top from html (base offset) + element's scroll-margin-top (extra offset)
    const htmlStyles = window.getComputedStyle(document.documentElement)
    const scrollPaddingTop = parseFloat(htmlStyles.scrollPaddingTop) || 0
    const elementStyles = window.getComputedStyle(element)
    const scrollMarginTop = parseFloat(elementStyles.scrollMarginTop) || 0
    const totalOffset = scrollPaddingTop + scrollMarginTop

    const offsetPosition = elementPosition + window.pageYOffset - totalOffset

    window.scrollTo({
      top: offsetPosition,
      behavior: 'smooth',
    })

    console.log('[TopicScroller] Scrolling to element:', element, 'at position:', offsetPosition, 'total offset:', totalOffset)
  }

  /**
   * Update the track bounds and circle position
   * @param {HTMLElement} activeElement - The active element (area heading or topic button)
   */
  updateTrackPosition(activeElement) {
    const areaItem = activeElement.closest('.area-item')
    if (!areaItem) return

    const trackWrapper = areaItem.querySelector('.area-track-wrapper')
    if (!trackWrapper) return

    const areaHeading = areaItem.querySelector('.area-heading')
    const topicsList = areaItem.querySelector('.topics-list')

    if (!areaHeading) return

    // Get positions relative to area item
    const areaItemRect = areaItem.getBoundingClientRect()
    const areaHeadingRect = areaHeading.getBoundingClientRect()

    // Calculate track start (center of area heading vertically)
    const areaHeadingTop = areaHeadingRect.top - areaItemRect.top
    const trackStart = areaHeadingTop + (areaHeadingRect.height / 2)

    // Calculate track end (center of last topic, or area heading center if no topics)
    let trackEnd = trackStart // Default to area heading center

    if (topicsList) {
      const topicButtons = topicsList.querySelectorAll('.topic-tag')
      if (topicButtons.length > 0) {
        // Use center of last topic
        const lastTopic = topicButtons[topicButtons.length - 1]
        const lastTopicRect = lastTopic.getBoundingClientRect()
        const lastTopicTop = lastTopicRect.top - areaItemRect.top
        trackEnd = lastTopicTop + (lastTopicRect.height / 2)
      }
    }

    // Set track dimensions
    const trackHeight = trackEnd - trackStart
    trackWrapper.style.setProperty('--track-start', `${trackStart}px`)
    trackWrapper.style.setProperty('--track-height', `${trackHeight}px`)

    // Calculate circle position (center of active element)
    const activeElementRect = activeElement.getBoundingClientRect()
    const activeElementTop = activeElementRect.top - areaItemRect.top
    const activeElementCenter = activeElementTop + (activeElementRect.height / 2)

    // Indicator position relative to track start
    const indicatorPosition = activeElementCenter - trackStart

    trackWrapper.style.setProperty('--indicator-position', `${indicatorPosition}px`)

    console.log('[TopicScroller] Track start:', trackStart, 'height:', trackHeight, 'indicator:', indicatorPosition)
  }

  /**
   * Setup scroll listener to update active topic based on scroll position
   */
  setupScrollListener() {
    let ticking = false

    const onScroll = () => {
      if (!ticking) {
        window.requestAnimationFrame(() => {
          this.updateActiveTopicOnScroll()
          ticking = false
        })
        ticking = true
      }
    }

    window.addEventListener('scroll', onScroll, { passive: true })
  }

  /**
   * Update which topic button is active based on current scroll position
   */
  updateActiveTopicOnScroll() {
    const topicsLists = this.listNavEl.querySelectorAll('.topics-list')

    // Get scroll-padding-top from html as offset
    const htmlStyles = window.getComputedStyle(document.documentElement)
    const scrollPaddingTop = parseFloat(htmlStyles.scrollPaddingTop) || 100

    topicsLists.forEach(topicsList => {
      const topicButtons = topicsList.querySelectorAll('[data-topic-anchor]')
      if (topicButtons.length === 0) return

      const scrollPosition = window.pageYOffset + scrollPaddingTop + 100

      let activeButton = null
      let minDistance = Infinity

      topicButtons.forEach(button => {
        const anchor = button.getAttribute('data-topic-anchor')
        const targetSelector = anchor.startsWith('#') ? anchor : `#${anchor}`
        const target = document.querySelector(targetSelector)

        if (!target) {
          // Try data-topic fallback
          const topicHandle = anchor.replace('#', '')
          const topicSection = document.querySelector(`[data-topic="${topicHandle}"]`)
          if (!topicSection) return

          const sectionTop = topicSection.offsetTop
          const distance = Math.abs(scrollPosition - sectionTop)

          if (distance < minDistance) {
            minDistance = distance
            activeButton = button
          }
        } else {
          const targetTop = target.offsetTop
          const distance = Math.abs(scrollPosition - targetTop)

          if (distance < minDistance) {
            minDistance = distance
            activeButton = button
          }
        }
      })

      // Update active state
      if (activeButton) {
        topicButtons.forEach(btn => btn.classList.remove('is-active'))
        activeButton.classList.add('is-active')
        this.updateTrackPosition(activeButton)
      }
    })
  }
}
